/**
 * CL-SHIP-02 — the shipping pricing rules, shared by the browser and the
 * Express server.
 *
 * This file is deliberately CommonJS and has ZERO dependencies: no SDK, no
 * React, no template import. `server/` is CommonJS and cannot import the ESM
 * `shipping.js`, so the pure arithmetic lives here and `shipping.js` imports
 * and re-exports it. There is exactly ONE definition of the fee formula.
 * Duplicating it is how a mirror drifts.
 *
 * It has to live under `src/` because the webpack build runs
 * ModuleScopePlugin(paths.appSrc) (config/webpack.config.js:336), which makes
 * any client import from outside `src/` a build error. The server reaches it
 * with a relative require. Do not move it to `server/` or a new top-level
 * directory, and do not give it a dependency.
 *
 * Amounts are integer currency subunits (USD cents). 0 is a valid amount and
 * means free shipping, so never use a falsy check on one.
 */

const SHIPPING_CONFIG_KEY = 'shippingConfig';
const SHIPPING_MODE_FLAT = 'flat-seller-entered';

const ZONE_DOMESTIC = 'domestic';
const ZONE_INTERNATIONAL = 'international';
const ZONE_UNKNOWN = 'unknown';

const FEE_PRICED = 'priced';
const FEE_BLOCKED = 'blocked';
const FEE_NOT_PRICED = 'not-priced';

// CL-SHIP-02 statuses. priceForZone() never returns these — only
// resolveShippingFee() does, because only it knows WHY a zone is unknown.
// That distinction is the difference between "ask the buyer" and "the listing
// data is broken", and they must not be handled alike.
const FEE_AWAITING_DESTINATION = 'awaiting-destination';
const FEE_NOT_OFFERED = 'not-offered';

/** A valid stored amount: a whole number of subunits, zero allowed. */
const isSubunitAmount = value => Number.isInteger(value) && value >= 0;

/** An ISO 3166-1 alpha-2 code, uppercased, or null. Never a display name. */
const normalizeCountryCode = value =>
  typeof value === 'string' && value.trim() ? value.trim().toUpperCase() : null;

/**
 * @param {string|null} originCountry - ISO 3166-1 alpha-2
 * @param {string|null} destinationCountry - ISO 3166-1 alpha-2
 */
const resolveShippingZone = (originCountry, destinationCountry) => {
  if (!originCountry || !destinationCountry) return ZONE_UNKNOWN;
  return originCountry.toUpperCase() === destinationCountry.toUpperCase()
    ? ZONE_DOMESTIC
    : ZONE_INTERNATIONAL;
};

const zoneAmount = (zoneConfig, quantity) => {
  const first = zoneConfig?.firstItem;
  const additional = zoneConfig?.additionalItem;
  if (!isSubunitAmount(first)) return null;
  if (quantity <= 1) return first;
  if (!isSubunitAmount(additional)) return null;
  return first + additional * (quantity - 1);
};

/**
 * The fee for one listing in one zone. Never throws: a caller in a checkout
 * path gets a status it can act on instead of a 500.
 *
 * @returns {{status: string, amountInSubunits: number|null}}
 */
const priceForZone = (shippingConfig, zone, quantity = 1) => {
  if (zone === ZONE_UNKNOWN) return { status: FEE_NOT_PRICED, amountInSubunits: null };

  if (zone === ZONE_INTERNATIONAL && !shippingConfig?.international?.enabled) {
    return { status: FEE_BLOCKED, amountInSubunits: null };
  }

  const zoneConfig =
    zone === ZONE_DOMESTIC ? shippingConfig?.domestic : shippingConfig?.international;
  const amount = zoneAmount(zoneConfig, quantity);

  return amount === null
    ? { status: FEE_NOT_PRICED, amountInSubunits: null }
    : { status: FEE_PRICED, amountInSubunits: amount };
};

/**
 * Read the shipping configuration. Falls back to a read-only view built from
 * the built-in template keys, so listings created before this feature (or
 * imported) still render and still price a single item.
 *
 * `shippingEnabled` is a stock template field that the delivery form always
 * writes together with the price pair, but data written outside that form
 * (an older listing, an import) can carry a usable price pair without ever
 * having set the flag. An explicit `false` is still the seller's authoritative
 * opt-out — honoured even if a stale price lingers — but its mere absence must
 * not hide a real, stored price. The price is the more direct signal.
 *
 * @returns {Object|null} null when the listing does not offer shipping
 */
const getShippingConfig = publicData => {
  const stored = publicData?.[SHIPPING_CONFIG_KEY];
  if (stored?.v) return stored;

  const shippingEnabled = publicData?.shippingEnabled;
  const first = publicData?.shippingPriceInSubunitsOneItem;
  const additional = publicData?.shippingPriceInSubunitsAdditionalItems;
  const hasLegacyPrice = isSubunitAmount(first);

  if (shippingEnabled === false || (!shippingEnabled && !hasLegacyPrice)) return null;

  return {
    v: 0,
    mode: SHIPPING_MODE_FLAT,
    currency: null,
    origin: null,
    domestic: {
      enabled: true,
      firstItem: isSubunitAmount(first) ? first : null,
      additionalItem: isSubunitAmount(additional) ? additional : null,
    },
    international: { enabled: false, firstItem: null, additionalItem: null, excludedCountries: [] },
  };
};

/**
 * The zone for a LISTING, as opposed to `resolveShippingZone`, which answers it
 * for a pair of countries and cannot know about a listing with no origin.
 *
 * Extracted from `resolveShippingFee` so a basket can reach the same answer.
 * `calculateCartShipping` called `resolveShippingZone` directly at first and got
 * ZONE_UNKNOWN for every pre-CL-SHIP-01 listing — which is every listing on Live,
 * since none has a `shippingConfig`. Buy It Now priced them and the basket
 * refused to, breaking AC-19 for the entire catalogue. One definition, so the two
 * paths cannot answer differently.
 *
 * @param {Object|null} shippingConfig - from getShippingConfig
 * @param {string|null} destinationCountry - ISO 3166-1 alpha-2
 */
const resolveZoneForConfig = (shippingConfig, destinationCountry) => {
  const originCountry = normalizeCountryCode(shippingConfig?.origin?.countryCode);
  // No origin means no zone can ever be resolved, and the only price such a
  // listing states is the domestic pair — exactly what the untouched template
  // charged. Keep charging it rather than refusing to price the back catalogue.
  return originCountry
    ? resolveShippingZone(originCountry, normalizeCountryCode(destinationCountry))
    : ZONE_DOMESTIC;
};

/**
 * The single decision point for one listing's shipping fee. Every caller —
 * browser and server — goes through here, so the price shown and the price
 * charged cannot disagree (decision D3).
 *
 * @param {Object} params
 * @param {Object} params.publicData - listing publicData
 * @param {string|null} params.destinationCountry - the buyer's delivery country (D5)
 * @param {number} params.quantity
 * @returns {{status: string, amountInSubunits: number|null, zone: string|null,
 *            originCountry: string|null, destinationCountry: string|null}}
 */
const resolveShippingFee = ({ publicData, destinationCountry, quantity = 1 }) => {
  const shippingConfig = getShippingConfig(publicData);
  if (!shippingConfig) {
    return {
      status: FEE_NOT_OFFERED,
      amountInSubunits: null,
      zone: null,
      originCountry: null,
      destinationCountry: null,
    };
  }

  const originCountry = normalizeCountryCode(shippingConfig.origin?.countryCode);
  const destination = normalizeCountryCode(destinationCountry);
  const units = Number.isInteger(quantity) && quantity > 0 ? quantity : 1;

  // See resolveZoneForConfig for the no-origin rule. Shared with the basket path
  // so a legacy listing cannot price one way singly and another way in a cart.
  const zone = resolveZoneForConfig(shippingConfig, destination);

  if (zone === ZONE_UNKNOWN) {
    return {
      status: FEE_AWAITING_DESTINATION,
      amountInSubunits: null,
      zone,
      originCountry,
      destinationCountry: destination,
    };
  }

  // Reaching priceForZone means the zone IS resolved, so a FEE_NOT_PRICED from
  // here can only mean the stored amounts are incomplete — a data fault, not a
  // missing address. Callers that charge money must treat it as an error.
  const { status, amountInSubunits } = priceForZone(shippingConfig, zone, units);

  return { status, amountInSubunits, zone, originCountry, destinationCountry: destination };
};

module.exports = {
  SHIPPING_CONFIG_KEY,
  SHIPPING_MODE_FLAT,
  ZONE_DOMESTIC,
  ZONE_INTERNATIONAL,
  ZONE_UNKNOWN,
  FEE_PRICED,
  FEE_BLOCKED,
  FEE_NOT_PRICED,
  FEE_AWAITING_DESTINATION,
  FEE_NOT_OFFERED,
  isSubunitAmount,
  normalizeCountryCode,
  resolveShippingZone,
  resolveZoneForConfig,
  priceForZone,
  getShippingConfig,
  resolveShippingFee,
};
